/**
 * Branded types for event external IDs.
 *
 * These are TypeScript brands that make `TxnEventExternalId` and
 * `ActionEventExternalId` distinct from each other and from plain `string`,
 * so that values cannot be silently mixed at the type level. At runtime they
 * are plain strings.
 *
 * This mirrors pensieve's `models.TxnEventExternalId` / `ActionEventExternalId`
 * NewTypes on the backend (see SC-116043), and helps catch the kind of
 * mismatch that previously had to be guarded at runtime by an Event-table
 * lookup in the umbridge case constructor.
 *
 * Use the lowercase constructor functions at boundaries where you know the
 * value is the right kind of event id — for example, after extracting
 * `external_id` from a `ShortTxnResponse` (which is by construction a txn
 * event) or `ShortActionEventResponse` (action event):
 *
 *     events.map((e) => txnEventExternalId(e.external_id))
 */

declare const TxnEventExternalIdBrand: unique symbol;
declare const ActionEventExternalIdBrand: unique symbol;

export type TxnEventExternalId = string & {
  readonly [TxnEventExternalIdBrand]: never;
};

export type ActionEventExternalId = string & {
  readonly [ActionEventExternalIdBrand]: never;
};

export const txnEventExternalId = (value: string): TxnEventExternalId =>
  value as TxnEventExternalId;

export const actionEventExternalId = (value: string): ActionEventExternalId =>
  value as ActionEventExternalId;
